import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergySwitchPanelCircuit,
  useEnergySwitchPanelCircuitDetailQuery,
  useEnergySwitchPanelCircuitDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

interface EnergySwitchPanelCircuitProps {
  id: string;
  onInfoUpdate?: (info: DeviceInformation) => void;
}

export const EnergySwitchPanelCircuitComponent: React.FC<
  EnergySwitchPanelCircuitProps
> = ({ id, onInfoUpdate }) => {
  const [device, setDevice] = useState<Partial<EnergySwitchPanelCircuit>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation>();

  const { data } = useEnergySwitchPanelCircuitDetailQuery({
    variables: { id },
  });

  useEnergySwitchPanelCircuitDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energySwitchPanelCircuitById) {
      setDevice(
        data.energySwitchPanelCircuitById as Partial<EnergySwitchPanelCircuit>,
      );
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation = {
        'Circuit Information': {
          'Circuit Name': device.name ?? 'Unnamed Circuit',
          'Circuit ID': device.deviceId ?? 'Unidentified',
          'Circuit Type': device.typeDisplayName ?? 'Unknown',
          'Last Updated': moment(device.timestamp).fromNow(),
        },
        'Energy Metrics': {
          'Energy Consumed': formatDeviceMetric({
            value: device.energyConsumed?.value ?? undefined,
            units: device.energyConsumed?.units ?? undefined,
          }),
          'Power Demand': formatDeviceMetric({
            value: device.powerDemand?.value ?? undefined,
            units: device.powerDemand?.units ?? undefined,
          }),
        },
        'Power Quality': {
          Voltage: formatDeviceMetric({
            value: device.voltage?.value ?? undefined,
            units: device.voltage?.units ?? undefined,
          }),
          Current: formatDeviceMetric({
            value: device.current?.value ?? undefined,
            units: device.current?.units ?? undefined,
          }),
          'Power Factor': formatDeviceMetric({
            value: device.powerFactor?.value ?? undefined,
            units: device.powerFactor?.units ?? undefined,
          }),
          Frequency: formatDeviceMetric({
            value: device.frequency?.value ?? undefined,
            units: device.frequency?.units ?? undefined,
          }),
        },
        'System Status': {
          'Power State': device.powerState?.value ? 'On' : 'Off',
          'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
          'Is Configured': device.isConfigured ? 'Yes' : 'No',
          'Is Controlled': device.isControlled ? 'Yes' : 'No',
          'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
          'Output Enabled': device.outputEnabled?.value ? 'Yes' : 'No',
        },
      };
      setDeviceInfo(info);
      if (onInfoUpdate) {
        onInfoUpdate(info);
      }
    }
  }, [device, onInfoUpdate]);

  if (!device) {
    return <DeviceLoadingSkeleton size="small" />;
  }

  return (
    <Box
      sx={{
        p: 1,
        m: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: device.outputEnabled?.value
          ? 'background.paper'
          : 'action.disabledBackground',
      }}
    >
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" color="textSecondary" align="center">
            Power Demand
          </Typography>
          <Typography variant="h6" align="center">
            {formatDeviceMetric({
              value: device.powerDemand?.value ?? undefined,
              units: device.powerDemand?.units ?? undefined,
            })}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" color="textSecondary" align="center">
            Current
          </Typography>
          <Typography variant="h6" align="center">
            {formatDeviceMetric({
              value: device.current?.value ?? undefined,
              units: device.current?.units ?? undefined,
            })}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" color="textSecondary" align="center">
            Status
          </Typography>
          <Typography variant="h6" align="center">
            {device.outputEnabled?.value ? 'Enabled' : 'Disabled'}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
